import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Edit a company you registered (Kloepfer K8-4, Larry 2026-10-08): the pencil
// on an Owner / Consultant / Subcontractor card opens this pop-up with the
// company's name and address; Save writes them and the card's name follows.
// Owner details (K9-8, Larry 2026-10-13): the pop-up also holds the contact
// (person, email, phone), and a button with data-company-editable="false"
// (the eye on the Owner row) opens the same details read-only.
// Shares the element with the `modal` controller (backdrop, Escape, close).
export default class extends Controller {
  static targets = ["name", "address", "country", "state", "city", "zip", "error", "saveBtn",
                    "contactName", "contactEmail", "contactPhone", "title", "form", "view", "cancelBtn",
                    "viewName", "viewAddress", "viewContact", "viewEmail", "viewPhone",
                    "viewPeopleBlock", "viewPeople", "emptyValue"]
  static values = { urlTemplate: String }

  connect() {
    // One listener for every pencil on the page, cards added later included.
    this._onEdit = (e) => {
      const btn = e.target.closest("[data-registered-company-edit]")
      if (!btn) return
      e.preventDefault()
      e.stopPropagation()
      this.open(btn)
    }
    document.addEventListener("click", this._onEdit)
  }

  disconnect() { document.removeEventListener("click", this._onEdit) }

  async open(btn) {
    this._btn = btn
    const d = btn.dataset
    this._id = d.companyId
    // Buttons from before K9-8 carry no flag: those are always the pencil.
    const editable = d.companyEditable !== "false"
    this._setMode(editable)
    this._trackOpen(d, editable)
    if (!editable) {
      this._fillView(d)
      this._modal()?.open()
      return
    }
    this.nameTarget.value = d.companyName || ""
    this.addressTarget.value = d.companyAddress || ""
    this.cityTarget.value = d.companyCity || ""
    this.zipTarget.value = d.companyZip || ""
    this.countryTarget.value = (d.companyCountry || "").toLowerCase()
    this.contactNameTarget.value = d.companyContactName || ""
    this.contactEmailTarget.value = d.companyContactEmail || ""
    this.contactPhoneTarget.value = d.companyContactPhone || ""
    this.errorTarget.classList.add("hidden")
    this.saveBtnTarget.disabled = false
    this._modal()?.open()
    this.nameTarget.focus()
    await this.countryChanged(d.companyState || "")
  }

  // Edit (the form + Save) or read-only (the details + Close).
  _setMode(editable) {
    this.formTarget.classList.toggle("hidden", !editable)
    this.viewTarget.classList.toggle("hidden", editable)
    this.saveBtnTarget.classList.toggle("hidden", !editable)
    if (this.hasTitleTarget) this.titleTarget.textContent = editable ? this.titleTarget.dataset.editTitle : this.titleTarget.dataset.viewTitle
    if (this.hasCancelBtnTarget) this.cancelBtnTarget.textContent = editable ? this.cancelBtnTarget.dataset.editLabel : this.cancelBtnTarget.dataset.viewLabel
  }

  // The read-only details, written as text (never HTML).
  _fillView(d) {
    this._setText(this.viewNameTarget, d.companyName)
    this._setText(this.viewAddressTarget, d.companyFullAddress)
    this._setText(this.viewContactTarget, d.companyContactName)
    this._setText(this.viewEmailTarget, d.companyContactEmail)
    this._setText(this.viewPhoneTarget, d.companyContactPhone)

    let people = []
    try { people = JSON.parse(d.companyPeople || "[]") } catch (e) { people = [] }
    this.viewPeopleTarget.replaceChildren()
    people.forEach(p => {
      const li = document.createElement("li")
      if (p.name) {
        li.textContent = p.name
        if (p.email) {
          const mail = document.createElement("span")
          mail.className = "text-gray-500"
          mail.textContent = ` · ${p.email}`
          li.appendChild(mail)
        }
      } else {
        li.textContent = p.email || ""
      }
      this.viewPeopleTarget.appendChild(li)
    })
    this.viewPeopleBlockTarget.classList.toggle("hidden", people.length === 0)
  }

  _setText(el, value) {
    const text = (value || "").trim()
    if (text) { el.textContent = text; return }
    el.replaceChildren(this.emptyValueTarget.content.cloneNode(true))
  }

  _trackOpen(d, editable) {
    try {
      track("Company Details Opened", {
        company_id: Number(d.companyId) || null,
        project_id: Number(d.projectId) || null,
        project_phase: d.projectPhase || null,
        mode: editable ? "edit" : "view",
        source: d.source || "manage_team"
      })
    } catch (e) { /* tracking never blocks the pop-up */ }
  }

  // The states of the chosen country, like the Register New Company pop-up.
  async countryChanged(selected = "") {
    const code = this.countryTarget.value
    const select = this.stateTarget
    const keep = typeof selected === "string" ? selected : ""
    select.innerHTML = '<option value="">Select...</option>'
    if (!code) return
    try {
      const resp = await fetch(`/locations/subregions?country_code=${encodeURIComponent(code)}`, { headers: { Accept: "application/json" } })
      const subregions = await resp.json()
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.code
        opt.textContent = sr.name
        select.appendChild(opt)
      })
      if (keep) select.value = keep
    } catch (e) { /* no list: the state stays as it was */ }
  }

  enterSaves(e) { if (e.key === "Enter") { e.preventDefault(); this.save() } }

  async save() {
    const name = this.nameTarget.value.trim()
    if (!name) return this._error("Give the company a name.")
    this.saveBtnTarget.disabled = true
    try {
      const resp = await fetch(this.urlTemplateValue.replace("__ID__", this._id), {
        method: "PATCH", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ company: {
          name, address: this.addressTarget.value.trim(), country_code: this.countryTarget.value,
          sub_country_code: this.stateTarget.value, city: this.cityTarget.value.trim(), zipcode: this.zipTarget.value.trim(),
          primary_contact_name: this.contactNameTarget.value.trim(), primary_contact_email: this.contactEmailTarget.value.trim(),
          primary_contact_phone: this.contactPhoneTarget.value.trim()
        } })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || (data.errors || []).join(", ") || "Could not save the company")
      this._paintCard(data)
      this._modal()?.close()
      this._toast("Company updated")
    } catch (err) {
      this._error(err.message)
      this.saveBtnTarget.disabled = false
    }
  }

  // The card follows: the name in its search box (or dropdown), the Team
  // card's row name, and every button's own data for the next open.
  _paintCard(data) {
    const btn = this._btn
    const card = btn?.closest('[data-controller~="team-card"]')
    const input = card?.querySelector('[data-team-card-target="companyInput"]')
    if (input?.tagName === "INPUT") input.value = data.name
    else if (input?.tagName === "SELECT") { const opt = [...input.options].find(o => o.value === String(data.id)); if (opt) opt.textContent = data.name }
    if (card) card.dataset.teamCardInitialCompanyNameValue = data.name
    document.querySelectorAll(`[data-company-name-for="${data.id}"]`).forEach(el => { el.textContent = data.name })
    const fresh = { companyName: data.name || "", companyAddress: data.address || "", companyCountry: data.country_code || "",
      companyState: data.sub_country_code || "", companyCity: data.city || "", companyZip: data.zipcode || "",
      companyFullAddress: data.full_address || "", companyContactName: data.primary_contact_name || "",
      companyContactEmail: data.primary_contact_email || "", companyContactPhone: data.primary_contact_phone || "" }
    const buttons = new Set(document.querySelectorAll(`[data-registered-company-edit][data-company-id="${data.id}"]`))
    if (btn) buttons.add(btn)
    buttons.forEach(b => Object.assign(b.dataset, fresh))
  }

  _modal() { return this.application.getControllerForElementAndIdentifier(this.element, "modal") }

  _error(msg) { this.errorTarget.textContent = msg; this.errorTarget.classList.remove("hidden") }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }
}
